import { fetchMercury } from '@/api/mercury/fetch'
import storage from '@/utils/storage'
import { withAccessToken } from '@/api/mercury/account'

/**
 * Represents the Image model.
 */
interface Image {
  alt: string
  attachmentContentType: string
  attachmentFileName: string
  attachmentHeight: number
  attachmentUpdatedAt: string
  attachmentWidth: number
  id: number
  largeUrl: string
  miniUrl: string
  position: number
  productUrl: string
  smallUrl: string
  type: string
  viewableId: number
  viewableType: string
}

/**
 * Represents the Adjustment model.
 */
interface Adjustment {
  adjustableId: number
  adjustableType: string
  amount: string
  createdAt: string
  displayAmount: string
  eligible: boolean
  finalized: boolean
  id: number
  label: string
  promotionCodeId: number
  sourceId: number
  sourceType: string
  updatedAt: string
}

/**
 * Represents the payment method
 */
interface PaymentMethod {
  id: number
  methodType: string
  name: string
  partialName: string
}

/**
 * Represents the billing address.
 */
interface BillAddress {
  address1: string
  address2: string
  alternativePhone: string
  city: string
  company: string
  country: {
    id: number
    iso: string
    iso3: string
    isoName: string
    name: string
    numcode: number
  }
  countryId: number
  countryIso: string
  id: number
  name: string
  phone: string
  state: {
    abbr: string
    countryId: number
    id: number
    name: string
  }
  stateId: number
  stateName: string
  stateText: string
  zipcode: string
}

/**
 * Represents the shipping address.
 */
interface ShipAddress {
  address1: string
  address2: string
  alternativePhone: string
  city: string
  company: string
  country: {
    id: number
    iso: string
    iso3: string
    isoName: string
    name: string
    numcode: number
  }
  countryId: number
  countryIso: string
  id: number
  name: string
  phone: string
  state: {
    abbr: string
    countryId: number
    id: number
    name: string
  }
  stateId: number
  stateName: string
  stateText: string
  zipcode: string
}

/**
 * Represents payments information.
 */
interface Payment {
  amount: string
  avsResponse: string
  createdAt: string
  displayAmount: string
  id: number
  paymentMethod: {
    id: number
    methodType: string
    name: string
    partialName: string
  }
  paymentMethodId: number
  source: {
    ccType: string
    gatewayCustomerProfileId: string
    gatewayPaymentProfileId: string
    id: number
    lastDigits: string
    month: string
    name: string
    year: string
  }
  sourceId: number
  sourceType: number
  state: string
  updatedAt: string
}

interface ShipmentManifest {
  quantity: number
  states: {
    onHand: number
  }
  variantId: number
}

interface ShippingRate {
  cost: string
  displayCost: string
  id: number
  name: string
  selected: boolean
  shippingMethodCode: string
  shippingMethodId: number
}

interface ShippingMethodCategory {
  id: number
  name: string
}

interface ShippingMethodZone {
  description: string
  id: number
  name: string
}

interface ShippingMethod {
  code: string
  id: number
  name: string
  shippingCategories: ShippingMethodCategory[]
  zones: ShippingMethodZone[]
}

interface Shipment {
  adjustments: Adjustment[]
  cost: string
  id: number
  manifest: ShipmentManifest[][]
  number: string
  orderId: string
  selectedShippingRate: ShippingRate
  shippedAt: string
  shippingMethods: ShippingMethod[]
  shippingRates: ShippingRate[]
  state: string
  stockLocationName: string
  tracking: string
  trackingUrl: string
}

interface CreditCard {
  address: unknown
  ccType: string
  id: number
  lastDigits: string
  month: string
  name: string
  year: string
}

/**
 * Defines action to take for proof service data when a line item is destroyed.
 * This is inherited from parents unless explicitly overridden.
 * Cancel is the equivalent of a soft-delete.
 * `null` means do nothing.
 */
export type LineItemOpDestroyProofAction = 'Cancel' | 'Delete' | null

/**
 * Operation to upsert, delete, or move a line item and its children.
 */
export interface LineItemOp {
  /**
   * Only valid on update. This must exist in the target order. Takes precedence as the identifier vs `uuid`.
   */
  lineItemId?: number
  /**
   * Identifies a line item if `lineItemId` isn't passed.
   * If `lineItemId` is passed, `uuid` will overwrite an existing value.
   */
  uuid?: string
  /**
   * Makes this a child of the line item . Written directly to the line item row.
   */
  parentUuid?: string | null
  /**
   * Identifies a customized design (i.e. proof ID).
   * As of 6/2023, this will always match the `uuid`, but we will later add support to allow
   * a `customizationId` to be reused across multiple line items.
   */
  customizationId?: string | null
  /**
   * Required on insert. May update an existing line item.
   */
  variantId?: number
  /**
   * Quantity is set absolutely unless `_incrementQuantity: true`.
   */
  quantity?: number
  /**
   * For a child line item with a variant that has a quantitySyncBehavior defined,
   * the resulting synced quantity applied to this child line item will be multiplied
   * by quantitySyncMultiplier if defined. For example, if the parent line item is a Photo Book,
   * and this is a child line item that adds a charge for extra pages added to a single photo book,
   * the extra pages variant will have quantitySyncBehavior == 'MAIN',
   * and this line item's quantitySyncMultiplier will be set to the number of extra pages added.
   * If 3 Photo Books are ordered, each with 5 extra pages, the parent's quantity would 3,
   * and this line item's quantitySyncMultiplier would be 5,
   * causing the synced quantity (the number of extra pages the customer pays for) to be 15 == (5 * 3).
   */
  quantitySyncMultiplier?: number | null
  /**
   * Can be set to a date string (`YYYY-MM-DD`) to delay fulfilment (i.e. shipping or mailing).
   * By default, line items are shipped ASAP.
   * Setting this to a future date will to delay shipment for customers who want to place an order in advance.
   * For example, a customer could order Christmas Cards in early November,
   * and set this to a date in late November to delay shipping until after Thanksgiving.
   * If this is set to a date before the earliest possible ship date, or the previously set value
   * becomes after the earliest possible ship date over time, the backend will automatically clear this.
   */
  scheduledShipDate?: string | null
  /**
   * Ensures this line item and its children are saved for later.
   */
  savedForLater?: boolean
  /**
   * Children to upsert (no deletions without `_destroyExtraChildren: false`).
   */
  children?: LineItemOp[]
  /**
   * If 'true', current quantity is incremented by quantity (possibly negative).
   * By default ('undefined'), or if 'false', quantity (non-negative) is set absolutely.
   */
  _incrementQuantity?: boolean
  /**
   * Deletes the line item, and implicitly all children.
   */
  _destroy?: boolean
  /**
   * Any existing children not updated via the immediate `children` get deleted.
   */
  _destroyExtraChildren?: boolean
  /**
   * Overrides default behavior for updating proof service when a line item is destroyed.
   *
   * By default, a line item with a customizationId and no parent is assumed to be a personalized
   * design and will be canceled in proof service (i.e. a soft delete) along with all of its children.
   * The only time that proofs are deleted, are for children that are automatically
   * deleted by setting `_destroyExtraChildren: true` on a parent.
   *
   * The default behavior requires us to know all children of any destroyed line item,
   * or of any parent with `_destroyExtraChildren: true`.
   *
   * TODO: To relieve the frontend of needing to know about children in the current cart for destroys,
   *   the backend should directly integrate with proof service to sync cancel and delete server side.
   */
  _destroyProofAction?: LineItemOpDestroyProofAction
}

/**
 * An interface representing a product variant.
 */
interface Variant {
  /**
   * The unique identifier of the variant.
   */
  id: number
  name: string
  sku: string
  weight: string
  height: string
  width: string
  minQuantity: number
  maxQuantity: number
  cartBehavior: string
  formatName: string
  quantityIncrement: number
  depth: string
  /**
   * A flag indicating whether the variant is the master variant.
   */
  isMaster: boolean
  slug: string
  description: string
  /**
   * A flag indicating if the inventory of the variant is tracked.
   */
  trackInventory: boolean
  price: string
  displayPrice: string
  /**
   * The text representation of the variant's options.
   */
  optionsText: string
  /**
   * A flag indicating whether the variant is in stock.
   */
  inStock: boolean
  /**
   * A flag indicating whether the variant is backorderable.
   */
  isBackorderable: boolean
  totalOnHand: number
  isDestroyed: boolean
  /**
   * An array of option values associated with the variant.
   */
  optionValues: {
    id: number
    name: string
    presentation: string
    optionTypeName: string
    optionTypeId: number
    optionTypePresentation: string
  }[]
  /**
   * An array of images associated with the variant.
   */
  images: Image[]
  /**
   * The unique identifier of the product associated with the variant.
   */
  productId: number
}

export interface LineItem {
  id: number
  quantity: number
  price: number
  variantId: number
  uuid: string
  parentUuid: string
  quantitySyncMultiplier: number
  customizationId: string
  scheduledShipDate: string
  promisedShipDate: string
  singleDisplayAmount: string
  displayAmount: string
  total: number
  projectedShipDate: string | null
  variant: Variant
  adjustments: Adjustment[]
  createdAt: string
}

/**
 * Interface for the response from the API representing an order.
 */
export interface OrderResponse {
  /**
   * The unique identifier for the order.*/
  id: number
  /**
   * The order number, alphanumeric identifier.
   */
  number: string
  itemTotal: string
  total: string
  shipTotal: string
  /**
   * The current state of the order.
   */
  state: string
  /**
   * The total amount of adjustments applied to the order.
   */
  adjustmentTotal: string
  /**
   * The unique identifier of the user who placed the order.
   */
  userId: number
  createdAt: string
  updatedAt: string
  completedAt: string
  paymentTotal: string
  /**
   * The state of the shipment.
   */
  shipmentState: string
  /**
   * The state of the payment.
   */
  paymentState: string
  /**
   * The email address of the user who placed the order.
   */
  email: string
  /**
   * Any special instructions associated with the order.
   */
  specialInstructions: string
  /**
   * The channel through which the order was placed.
   */
  channel: string
  includedTaxTotal: string
  additionalTaxTotal: string
  displayIncludedTaxTotal: string
  displayAdditionalTaxTotal: string
  taxTotal: string
  currency: string
  /**
   * Indicates whether the order was covered by store credit.
   */
  coveredByStoreCredit: boolean
  /**
   * The display total applicable store credit amount.
   */
  displayTotalApplicableStoreCredit: string
  /**
   * The order total amount after store credit.
   */
  orderTotalAfterStoreCredit: string
  /**
   * The display order total amount after store credit.
   */
  displayOrderTotalAfterStoreCredit: string
  /**
   * The total applicable store credit amount.
   */
  totalApplicableStoreCredit: number
  /**
   * The display total available store credit amount.
   */
  displayTotalAvailableStoreCredit: string
  /**
   * The display store credit remaining after capture amount.
   */
  displayStoreCreditRemainingAfterCapture: string
  /**
   * The unique identifier of the user who cancelled the order.
   */
  cancelerId: number
  displayItemTotal: string
  totalQuantity: number
  displayTotal: string
  displayShipTotal: string
  displayTaxTotal: string
  token: string

  /**
   * The list of checkout steps for the order.
   */
  checkoutSteps: string[]

  /**
   * The list of payment methods available for the order.
   */
  paymentMethods: PaymentMethod[]

  /**
   * The billing address for the order, or null if not specified.
   */
  billAddress: BillAddress
  shipAddress: ShipAddress
  /**
   * The list of payments associated with the order.
   */
  payments: Payment[]
  /**
   * The list of shipments associated with the order.
   */
  shipments: Shipment[]
  adjustments: Adjustment[]
  /**
   * The permissions for the order, specifying whether it can be updated.
   */
  permissions: {
    canUpdate: boolean
  }
  /**
   * The list of credit cards associated with the order.
   */
  creditCards: CreditCard[]
  /**
   * Represents an array of line items in an order.
   */
  lineItems: LineItem[]
}

/**
 * Updates one or more line items in the cart with the provided operations.
 */
export function update(lineItemOps: LineItemOp[]): Promise<void> {
  return withAccessToken(async (accessToken: string) => {
    await withCurrentOrderNumber(accessToken, (orderNumber: string) =>
      updateRequest(orderNumber, lineItemOps, accessToken)
    )
  })
}

/**
 * Updates one or more line items with the provided operations.
 */
function updateRequest(
  orderNumber: string,
  lineItemOps: LineItemOp[],
  accessToken: string
): Promise<void> {
  return fetchMercury<void>({
    method: 'PATCH',
    path: `/mercury/api/orders/${orderNumber}/line_items/bulk_upsert`,
    accessToken,
    data: { lineItems: lineItemOps }
  })
}

/**
 * Creates a new order, preferred to be used when we have not a current order.
 */
function createOrderRequest(accessToken: string): Promise<OrderResponse> {
  return fetchMercury<OrderResponse>({
    method: 'POST',
    path: '/mercury/api/orders',
    accessToken
  })
}

/**
 * A wrapper for methods which receive the current order number as a parameter.
 * It tries to get the current order number, and if it is not available,
 * it creates a new order to continue with the operations.
 * @param callback - A function that receives the current order number as a parameter.
 * @returns A Promise that resolves with the result of the callback.
 */
async function withCurrentOrderNumber<T>(
  accessToken: string,
  callback: (orderNumber: string) => Promise<T>
): Promise<T> {
  try {
    const orderNumber = await getCurrentOrderNumber(accessToken)
    if (!orderNumber) {
      const newOrder = await createOrderRequest(accessToken)
      return await callback(newOrder.number)
    }
    return await callback(orderNumber)
  } catch (error) {
    // TODO: Instead of Error, we're checking for something like CompletedOrderError
    if (error instanceof Error) {
      const orderNumber = await getCurrentOrderNumber(accessToken, true)
      return await callback(orderNumber as string)
    } else {
      throw error
    }
  }
}

/**
 * Gets the current order number if it exists locally, or gets from Mercury.
 * @param forceRefresh - A boolean indicating whether to force a refresh and get the order number directly from Mercury.
 * @returns A Promise that resolves with the current order number, or null if it doesn't exist.
 */
async function getCurrentOrderNumber(
  accessToken: string,
  forceRefresh = false
): Promise<string | null> {
  const currentOrderNumber = storage.getItem('currentOrderNumber')
  if (typeof currentOrderNumber !== 'string' || forceRefresh) {
    const currentOrder = await getCurrentOrderRequest(accessToken)
    if (currentOrder !== null) {
      const orderNumber = currentOrder.number
      storage.setItem('currentOrderNumber', orderNumber)
      return orderNumber
    } else {
      storage.removeItem('currentOrderNumber')
      return null
    }
  }
  return currentOrderNumber
}

/**
 * Make the API call to get the current order from Mercury.
 * @returns A Promise that resolves with the current order, or null if it doesn't exist.
 */
async function getCurrentOrderRequest(
  accessToken: string
): Promise<OrderResponse | null> {
  return (
    (await fetchMercury<OrderResponse>({
      path: '/mercury/api/orders/current',
      accessToken
    })) || null
  )
}

/**
 * Cart cache stores the promise which will returns the cart items when a call
 * is made in this way we avoid multiple api calls for get the same information.
 */
let cartResponseCache: Promise<LineItem[] | null> | null = null

/**
 * Clears the cart cache before any update, allowing us to get correct data from backend
 * after any change.
 */
export function clearCartCache(): void {
  cartResponseCache = null
}

/**
 * Get the current order from Mercury.
 * @returns A Promise that resolves with the current order, or null if it doesn't exist.
 */
export async function getCurrentOrder(): Promise<LineItem[] | null> {
  try {
    if (!cartResponseCache) {
      cartResponseCache = withAccessToken<LineItem[] | null>(
        async (accessToken) =>
          (await getCurrentOrderRequest(accessToken))?.lineItems || null
      )
    }
    return await cartResponseCache
  } catch (error) {
    // We clear cache at failure response, in this wey we are able to do a retry with a new response from backend
    clearCartCache()
    throw error
  }
}

/**
 * Make the API call to get the saved for later items from Mercury.
 * @returns A Promise that resolves with the saved for later items, or null if there is no items
 */
async function getSavedForLaterItemsRequest(
  accessToken: string
): Promise<OrderResponse | null> {
  return (
    (await fetchMercury<OrderResponse>({
      path: `/mercury/api/orders/saved_for_later`,
      accessToken
    })) || null
  )
}

/**
 * Saved for later cache stores the promise which will returns the saved for later items when a call
 * is made in this way we avoid multiple api calls for get the same information.
 */
let savedForLaterResponseCache: Promise<LineItem[] | null> | null = null

/**
 * Clears the saved for later cache before any update, allowing us to get correct data from backend
 * after any change.
 */
export function clearSavedForLaterCache(): void {
  savedForLaterResponseCache = null
}

/**
 * Get the list of saved items from Mercury.
 * @returns A Promise that resolves with the saved for later items, or null if it doesn't exist.
 */
export async function getSavedForLaterItems(): Promise<LineItem[] | null> {
  try {
    if (!savedForLaterResponseCache) {
      savedForLaterResponseCache = withAccessToken(
        async (accessToken) =>
          (await getSavedForLaterItemsRequest(accessToken))?.lineItems || null
      )
    }
    return await savedForLaterResponseCache
  } catch (error) {
    // We clear cache at failure response, in this wey we are able to do a retry with a new response from backend
    clearSavedForLaterCache()
    throw error
  }
}
